import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore";
import { navigationStore } from "@/store/pinia_modules/navigationStore";
import { ActionType } from "@/types/customPage";
import turf from "@turf/circle";
import type mapboxgl from "mapbox-gl";
import { DropZone, DropZoneRadius } from "@/types/dropZone";

export function getGeolocationPermissions(): Promise<string> {
	return new Promise((resolve) => {
		if (navigator.geolocation) {
			navigator.permissions
				.query({ name: "geolocation" })
				.then(function (result) {
					resolve(result.state);
				});
		} else {
			resolve("not supported");
		}
	});
}

export function getUserLocation() {
	const dropPoolState = dropPoolStore();

	const id = dropPoolState.getWatcher;

	if (id != -1) {
		navigator.geolocation.clearWatch(id);

		dropPoolState.saveWatcher(-1);
	}
	// Prompt permissions and seed initial location via one-shot request.
	navigator.geolocation.getCurrentPosition(
		(position: {
			coords: {
				latitude: number;
				longitude: number;
				accuracy: number;
			};
		}) => {
			dropPoolState.saveUserLocation([
				position.coords.longitude,
				position.coords.latitude,
			]);
			dropPoolState.saveLocationAccuracy(position.coords.accuracy);
		},
		(error) => {
			const errors: any = {
				1: "You have denied location permissions in your phone settings. Please allow location permissions in order to continue.",
				2: "Your position is currently not available",
				3: "Location request timeout error",
			};
			console.log(errors[error.code]);

			dropPoolState.loading = false;
			if (error.code == 1) {
				dropPoolState.saveLocationAccuracy(-1);
				navigationStore().saveDeniedPermissions("Geolocation" as never);
				navigationStore().emitAction({
					type: ActionType.GO_TO_PAGE,
					value: "permissions",
				});
			}
		},
		{ enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
	);
}

export function isInRadius(
	userLocation: [number, number],
	location: [number, number],
	radius: number
): boolean {
	const distance = calcCrow(
		userLocation[1],
		userLocation[0],
		location[1],
		location[0]
	);
	if (distance <= radius) {
		return true;
	} else {
		return false;
	}
}

export function calcCrow(
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number
): number {
	if (lat1 == lat2 && lon1 == lon2) {
		return 0;
	} else {
		const radlat1 = (Math.PI * lat1) / 180;
		const radlat2 = (Math.PI * lat2) / 180;
		const theta = lon1 - lon2;
		const radtheta = (Math.PI * theta) / 180;
		let dist =
			Math.sin(radlat1) * Math.sin(radlat2) +
			Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
		if (dist > 1) {
			dist = 1;
		}
		dist = Math.acos(dist);
		dist = (dist * 180) / Math.PI;
		dist = dist * 60 * 1.1515;
		dist = dist * 1.609344;
		return dist * 1000;
	}
}

export function getRandomLocations(
	center: [number, number],
	amount: number,
	radiusInMeters: number
): [number, number][] {
	const coordinates: [number, number][] = [];
	function addLocations() {
		if (amount > 0) {
			const random = randomCoordinateInRadius(center, radiusInMeters);
			const distance = calcCrow(center[1], center[0], random[0], random[1]);
			console.log(distance);
			if (distance > 10) {
				coordinates.push(random);
				amount--;
				addLocations();
			} else {
				addLocations();
			}
		}
	}
	addLocations();
	return coordinates;
}

function randomCoordinateInRadius(
	center: [number, number],
	radius: number
): [number, number] {
	const randomCoordinates = getRandomCoordinates(radius, true);
	const earth = 6378137;

	const northOffset = randomCoordinates[0],
		eastOffset = randomCoordinates[1];

	const offsetLatitude = northOffset / earth,
		offsetLongitude =
			eastOffset / (earth * Math.cos(Math.PI * (center[0] / 180)));

	return [
		center[1] + offsetLongitude * (180 / Math.PI),
		center[0] + offsetLatitude * (180 / Math.PI),
	];
}

function getRandomCoordinates(
	radius: number,
	uniform: boolean
): [number, number] {
	let a = Math.random(),
		b = Math.random();
	if (uniform) {
		if (b < a) {
			const c = b;
			b = a;
			a = c;
		}
	}
	return [
		b * radius * Math.cos((2 * Math.PI * a) / b),
		b * radius * Math.sin((2 * Math.PI * a) / b),
	];
}

export const addDropZoneCircles = (
	circles: { latitude: number; longitude: number; radius: number }[]
) => {
	const features: any[] = [];
	for (let i = 0; i < circles.length; i++) {
		features.push(
			turf([circles[i].longitude, circles[i].latitude], circles[i].radius, {
				units: "kilometers",
			})
		);
	}
	return {
		type: "geojson",
		data: {
			type: "FeatureCollection",
			features: features,
		},
	};
};

// Generate a geodesic circle feature from center+radius for rendering (no persistence)
function radiusToCircleFeature(zone: DropZoneRadius, steps = 64): GeoJSON.Feature<GeoJSON.Polygon> {
    const center = zone.geometry.coordinates; // [lng, lat]
    const radiusKm = (zone.properties.radiusMeters || 0) / 1000;
    const circle = turf(center as any, radiusKm, { steps, units: "kilometers" });
    return circle as unknown as GeoJSON.Feature<GeoJSON.Polygon>;
}

function zonesToFeatureCollection(zones: DropZone[]): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const features: GeoJSON.Feature[] = zones.map((z) => {
        if (z.type === "drop-zone-polygon") {
            return {
                type: "Feature",
                id: z.id,
                properties: {
                    ...z.properties,
                    zoneId: z.id,
                    zoneType: z.type,
                    activeFromTs: z.properties.activeFrom ? Date.parse(z.properties.activeFrom) : null,
                    inactiveFromTs: z.properties.inactiveFrom ? Date.parse(z.properties.inactiveFrom) : null,
                },
                geometry: z.geometry as unknown as GeoJSON.Polygon,
            } as GeoJSON.Feature<GeoJSON.Polygon>;
        } else {
            const poly = radiusToCircleFeature(z);
            (poly as any).id = z.id;
            (poly as any).properties = {
                ...z.properties,
                zoneId: z.id,
                zoneType: z.type,
                activeFromTs: z.properties.activeFrom ? Date.parse(z.properties.activeFrom) : null,
                inactiveFromTs: z.properties.inactiveFrom ? Date.parse(z.properties.inactiveFrom) : null,
            } as any;
            return poly as GeoJSON.Feature<GeoJSON.Polygon>;
        }
    });
    return { type: "FeatureCollection", features } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;
}

export function mountZonesOnMap(map: mapboxgl.Map, sourceId: string, zones: DropZone[], now = new Date()): void {
    const fc = zonesToFeatureCollection(zones);
    const nowTs = now.getTime();

    if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(fc as any);
    } else {
        map.addSource(sourceId, { type: "geojson", data: fc as any });
        map.addLayer({
            id: `${sourceId}-fill`,
            type: "fill",
            source: sourceId,
            paint: {
                "fill-color": [
                    "case",
                    [
                        "all",
                        ["<=", ["to-number", ["get", "activeFromTs"], 0], nowTs],
                        [">=", ["to-number", ["get", "inactiveFromTs"], 32503680000000], nowTs],
                    ],
                    "#4ade80",
                    "#a3a3a3",
                ],
                "fill-opacity": 0.25,
            },
            filter: ["all"],
        } as any);
        map.addLayer({
            id: `${sourceId}-line`,
            type: "line",
            source: sourceId,
            paint: { "line-color": "#10b981", "line-width": 2 },
        } as any);
        // Zone name labels removed
        // map.addLayer({
        //     id: `${sourceId}-symbol`,
        //     type: "symbol",
        //     source: sourceId,
        //     layout: { "text-field": ["get", "zoneName"], "text-size": 12 },
        //     paint: { "text-color": "#065f46" },
        // } as any);
    }

    const activeFilter: any = [
        "all",
        ["<=", ["to-number", ["get", "activeFromTs"], 0], nowTs],
        [">=", ["to-number", ["get", "inactiveFromTs"], 32503680000000], nowTs],
    ];
    try {
        (map as any).setFilter(`${sourceId}-fill`, activeFilter);
    } catch (_) {
        // if layer not present yet, ignore
    }
}

// Compute an approximate center [lng, lat] for a zone
export function getZoneCenter(zone: DropZone): [number, number] {
    if (zone.type === "drop-zone-radius") {
        return zone.geometry.coordinates as [number, number];
    }
    // Polygon: use bbox center if available; fallback to first vertex
    if (Array.isArray(zone.bbox) && zone.bbox.length === 4) {
        const [minLng, minLat, maxLng, maxLat] = zone.bbox;
        return [ (minLng + maxLng) / 2, (minLat + maxLat) / 2 ];
    }
    const first = (zone.geometry as any)?.coordinates?.[0]?.[0] as [number, number] | undefined;
    return Array.isArray(first) ? first : [0, 0];
}

// Return up to `limit` nearest zones to a given center [lng, lat]
export function selectNearestZones(zones: DropZone[], center: [number, number], limit = 6): DropZone[] {
    if (!zones || zones.length <= limit) return zones;
    const withDist = zones.map((z) => {
        const c = getZoneCenter(z);
        const dx = (c[0] - center[0]);
        const dy = (c[1] - center[1]);
        const d2 = dx * dx + dy * dy;
        return { z, d2 };
    });
    withDist.sort((a, b) => a.d2 - b.d2);
    return withDist.slice(0, limit).map((e) => e.z);
}
